/**
 * Copiloto de call — configuracoes.
 *
 * Tres abas: Playbook (o unico material que o copiloto usa), Regras (o que
 * vende, quando fala, palavras-gatilho) e Extensao (conectar a conta). A IA
 * ajuda a preencher — gera o playbook a partir de material colado, revisa um
 * trecho e sugere gatilhos — mas nunca grava sozinha: quem gerencia escolhe.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { PermissionGuard } from "@/components/PermissionGuard";
import DashboardLayout from "@/components/DashboardLayout";
import { toUserFriendlyError } from "@/lib/error-messages";
import { conectarExtensao, desconectarExtensao, detectarExtensao, type EstadoExtensao } from "@/lib/copiloto-ponte";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { BarChart3, BookOpen, ChevronDown, Chrome, Download, Loader2, Pencil, Plug, Plus, RefreshCw, Search, Sparkles, Trash2, X } from "lucide-react";
import {
  COPILOTO_CHUNK_TIPOS, COPILOTO_ESTAGIOS, COPILOTO_SUBTIPOS,
  type CopilotoChunkTipo, type CopilotoEstagio, type CopilotoGatilhos, type CopilotoPolitica, type CopilotoSubtipo,
} from "@shared/copiloto-contracts";

/** Pacote da extensão servido pelo próprio app (client/public/downloads); gerado por extension/publicar-no-app.sh. */
const EXTENSAO_DOWNLOAD = "/downloads/leadconversa-copiloto.zip";

const ROTULO_SUBTIPO: Record<string, string> = {
  preco: "Preço", timing: "Timing", autoridade: "Alçada", concorrente: "Concorrente",
  ceticismo: "Ceticismo", escopo: "Escopo", sinalCompra: "Sinal de compra",
};
const ROTULO_TIPO: Record<string, string> = {
  script: "Script", objecao: "Objeção", prova: "Prova", preco: "Preço", caso: "Caso", politica: "Política",
};
const ROTULO_ESTAGIO: Record<string, string> = {
  sondagem: "Sondagem", apresentacao: "Apresentação", proposta: "Proposta", objecao: "Objeção", fechamento: "Fechamento",
};

type Trecho = {
  /** null = empresa toda; id do produto do CRM = so nas calls daquele produto. */
  productId: number | null;
  titulo: string; tipo: CopilotoChunkTipo; estagios: CopilotoEstagio[]; tags: Exclude<CopilotoSubtipo, "nenhum">[];
  resumoBusca: string; conteudo: string; isActive: boolean;
};
const TRECHO_VAZIO: Trecho = { productId: null, titulo: "", tipo: "objecao", estagios: [], tags: [], resumoBusca: "", conteudo: "", isActive: true };
type Produto = { id: number; nome: string };
const ESCOPO_EMPRESA = "empresa";

/** Seletor "vale para": empresa toda ou um produto do CRM. */
function SelectEscopo({ valor, produtos, onChange, className }: { valor: number | null; produtos: Produto[]; onChange: (v: number | null) => void; className?: string }) {
  return (
    <Select value={valor === null ? ESCOPO_EMPRESA : String(valor)} onValueChange={(v) => onChange(v === ESCOPO_EMPRESA ? null : Number(v))}>
      <SelectTrigger className={className}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ESCOPO_EMPRESA}>Empresa (todas as calls)</SelectItem>
        {produtos.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.nome}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function alternar<T>(lista: T[], item: T): T[] {
  return lista.includes(item) ? lista.filter((x) => x !== item) : [...lista, item];
}

// ─── Chips: lista de termos curtos, digita e Enter ───────────────────────────

function Chips({ valor, onChange, disabled, placeholder }: { valor: string[]; onChange: (v: string[]) => void; disabled?: boolean; placeholder?: string }) {
  const [texto, setTexto] = useState("");
  const adicionar = () => {
    const termos = texto.split(/[,\n]/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (termos.length === 0) return;
    onChange([...valor, ...termos.filter((t) => !valor.includes(t))]);
    setTexto("");
  };
  const teclar = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); adicionar(); }
    if (e.key === "Backspace" && texto === "" && valor.length) onChange(valor.slice(0, -1));
  };
  return (
    <div className={`flex min-h-10 flex-wrap items-center gap-1 rounded-md border px-2 py-1 ${disabled ? "bg-muted/40" : "bg-background"}`}>
      {valor.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs">
          {t}
          {!disabled && <button type="button" aria-label={`remover ${t}`} onClick={() => onChange(valor.filter((x) => x !== t))}><X className="h-3 w-3" /></button>}
        </span>
      ))}
      {!disabled && (
        <input
          className="min-w-[120px] flex-1 bg-transparent px-1 py-1 text-sm outline-none"
          value={texto}
          placeholder={valor.length ? "" : placeholder}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={teclar}
          onBlur={adicionar}
        />
      )}
    </div>
  );
}

// ─── Aba: Extensao ───────────────────────────────────────────────────────────

function AbaExtensao({ wsUrl, usuario }: { wsUrl: string; usuario: string }) {
  const [estado, setEstado] = useState<EstadoExtensao | null | "procurando">("procurando");
  const emitir = trpc.copiloto.tokenExtensao.useMutation();

  const procurar = async () => {
    setEstado("procurando");
    setEstado(await detectarExtensao());
  };
  useEffect(() => { void procurar(); }, []);

  const conectar = async () => {
    try {
      const r = await emitir.mutateAsync();
      const ok = await conectarExtensao({ token: r.token, wsUrl: r.wsUrl, usuario: r.usuario, expiraEm: String(r.expiraEm) });
      if (!ok) { toast.error("A extensão não respondeu. Recarregue a página e tente de novo."); return; }
      toast.success("Extensão conectada à sua conta.");
      await procurar();
    } catch (err) {
      toast.error(toUserFriendlyError(err));
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base"><Chrome className="h-4 w-4" /> Extensão do Chrome</CardTitle>
        <CardDescription>Captura o áudio da reunião (Google Meet ou Zoom no navegador) e mostra o card com a sugestão. Fica ligada à sua conta.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {estado === "procurando" && <p className="text-sm text-muted-foreground">Procurando a extensão…</p>}
        {estado === null && (
          <div className="space-y-3 text-sm">
            <p className="font-medium">Extensão não encontrada neste navegador.</p>
            {/* Instalação manual enquanto a versão da Chrome Web Store está em revisão. Quando publicar, trocar por um link para a loja. */}
            <div className="flex flex-wrap items-center gap-2">
              <Button asChild size="sm">
                <a href={EXTENSAO_DOWNLOAD} download><Download className="mr-2 h-3.5 w-3.5" /> Baixar extensão (.zip)</a>
              </Button>
              <Button variant="outline" size="sm" onClick={procurar}><RefreshCw className="mr-2 h-3.5 w-3.5" /> Procurar de novo</Button>
            </div>
            <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
              <li>Baixe o arquivo acima e descompacte numa pasta que você não vá apagar (ex.: Documentos/copiloto).</li>
              <li>No Chrome, abra <code className="rounded bg-muted px-1">chrome://extensions</code>, ligue o <strong>Modo do desenvolvedor</strong> (canto superior direito) e clique em <strong>Carregar sem compactação</strong>, escolhendo a pasta descompactada.</li>
              <li>Volte aqui, recarregue a página e clique em <strong>Procurar de novo</strong>; depois em <strong>Conectar esta conta</strong>.</li>
            </ol>
            <p className="text-xs text-muted-foreground">O Chrome pode avisar "Desative as extensões no modo de desenvolvedor" ao abrir — pode fechar o aviso. Em breve a instalação será pela Chrome Web Store, sem esse passo.</p>
          </div>
        )}
        {estado && estado !== "procurando" && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <div className="flex items-center gap-2">
                <Badge variant="outline">v{estado.versao}</Badge>
                {estado.conectada
                  ? <span className="text-green-700">Conectada como <strong>{estado.usuario || usuario}</strong></span>
                  : <span className="text-amber-700">Instalada, mas ainda sem conta</span>}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Servidor: {wsUrl}</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={conectar} disabled={emitir.isPending}><Plug className="mr-2 h-4 w-4" /> {estado.conectada ? "Reconectar" : "Conectar esta conta"}</Button>
              {estado.conectada && <Button variant="outline" onClick={async () => { await desconectarExtensao(); toast.success("Extensão desconectada."); await procurar(); }}>Desconectar</Button>}
            </div>
          </div>
        )}
        <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
          Na reunião: clique em <strong>Ligar</strong> no card da extensão (ou no ícone dela, ou <kbd>Alt+Shift+C</kbd>). O ícone fica com um "ON" verde enquanto captura. Na primeira vez o Chrome pede o microfone. <strong>Socorro</strong> pede uma sugestão na hora.
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Aba: Regras (venda, politica, gatilhos) ─────────────────────────────────

type FormRegras = { isEnabled: boolean; produto: string; ticket: string; nomeEmpresa: string; politica: CopilotoPolitica; gatilhos: CopilotoGatilhos };

const POLITICA_PRINCIPAL: { chave: keyof CopilotoPolitica; rotulo: string; ajuda: string; passo?: number }[] = [
  { chave: "intervaloMinimoSeg", rotulo: "Silêncio entre sugestões (s)", ajuda: "Depois de um card, fica calado por este tempo." },
  { chave: "confiancaMinima", rotulo: "Confiança mínima (0 a 1)", ajuda: "Abaixo disto o detector não interrompe.", passo: 0.05 },
  { chave: "tetoPorHora", rotulo: "Máximo por hora", ajuda: "Teto duro de sugestões por hora de call." },
];
const POLITICA_AVANCADA: typeof POLITICA_PRINCIPAL = [
  { chave: "intervaloOutroSubtipoSeg", rotulo: "Objeção nova pode furar após (s)", ajuda: "Objeção de outro tipo, ainda não tratada, entra antes do silêncio acabar." },
  { chave: "janelaSeg", rotulo: "Janela de contexto (s)", ajuda: "Quantos segundos de conversa o detector enxerga." },
  { chave: "segundosSemGatilhoAteAvaliar", rotulo: "Reavaliar sem gatilho após (s)", ajuda: "Sem palavra-gatilho, o detector só volta a rodar depois deste tempo." },
];

function CampoPolitica({ c, form, setForm, ro }: { c: (typeof POLITICA_PRINCIPAL)[number]; form: FormRegras; setForm: (f: FormRegras) => void; ro: boolean }) {
  return (
    <div>
      <Label>{c.rotulo}</Label>
      <Input type="number" step={c.passo ?? 1} disabled={ro} value={form.politica[c.chave]}
        onChange={(e) => setForm({ ...form, politica: { ...form.politica, [c.chave]: Number(e.target.value) } })} />
      <p className="mt-1 text-xs text-muted-foreground">{c.ajuda}</p>
    </div>
  );
}

function AbaRegras({ podeGerenciar, temIa }: { podeGerenciar: boolean; temIa: boolean }) {
  const utils = trpc.useUtils();
  const cfg = trpc.copiloto.settings.get.useQuery();
  const salvar = trpc.copiloto.settings.update.useMutation({
    onSuccess: () => { void utils.copiloto.settings.get.invalidate(); void utils.copiloto.status.invalidate(); toast.success("Regras salvas."); },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });
  const sugerir = trpc.copiloto.ia.sugerirGatilhos.useMutation({ onError: (err) => toast.error(toUserFriendlyError(err)) });

  const [form, setForm] = useState<FormRegras | null>(null);
  const [avancado, setAvancado] = useState(false);
  const [sugestoes, setSugestoes] = useState<Partial<CopilotoGatilhos> | null>(null);
  const salvo = useRef<string>("");

  useEffect(() => {
    if (cfg.data && !form) {
      const f = { isEnabled: cfg.data.isEnabled, produto: cfg.data.produto, ticket: cfg.data.ticket, nomeEmpresa: cfg.data.nomeEmpresa, politica: cfg.data.politica, gatilhos: cfg.data.gatilhos };
      setForm(f);
      salvo.current = JSON.stringify(f);
    }
  }, [cfg.data, form]);

  if (!form) return <p className="text-sm text-muted-foreground">Carregando…</p>;
  const ro = !podeGerenciar;
  const sujo = JSON.stringify(form) !== salvo.current;

  const aplicarSugestao = (k: keyof CopilotoGatilhos, termo: string) => {
    setForm({ ...form, gatilhos: { ...form.gatilhos, [k]: [...form.gatilhos[k], termo] } });
    setSugestoes((s) => s && { ...s, [k]: (s[k] ?? []).filter((x) => x !== termo) });
  };
  const aplicarTodas = () => {
    if (!sugestoes) return;
    const g = { ...form.gatilhos };
    for (const k of Object.keys(sugestoes) as (keyof CopilotoGatilhos)[]) g[k] = [...g[k], ...(sugestoes[k] ?? []).filter((t) => !g[k].includes(t))];
    setForm({ ...form, gatilhos: g });
    setSugestoes(null);
  };

  return (
    <div className="space-y-4 pb-20">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">O que você vende</CardTitle>
          <CardDescription>Entra no prompt de toda call. Quanto mais concreto, melhor a sugestão.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label>Produto</Label>
              <Input value={form.produto} disabled={ro} placeholder="Ex: curso in company de licitações" onChange={(e) => setForm({ ...form, produto: e.target.value })} />
            </div>
            <div>
              <Label>Ticket típico</Label>
              <Input value={form.ticket} disabled={ro} placeholder="Ex: R$ 30 mil" onChange={(e) => setForm({ ...form, ticket: e.target.value })} />
            </div>
            <div>
              <Label>Nome da empresa</Label>
              <Input value={form.nomeEmpresa} disabled={ro} placeholder="Como o copiloto se apresenta" onChange={(e) => setForm({ ...form, nomeEmpresa: e.target.value })} />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <p className="text-sm font-medium">Copiloto ligado</p>
              <p className="text-xs text-muted-foreground">Desligado, a extensão é recusada para todo mundo do workspace.</p>
            </div>
            <Switch checked={form.isEnabled} disabled={ro} onCheckedChange={(v) => setForm({ ...form, isEnabled: v })} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Quando o copiloto fala</CardTitle>
          <CardDescription>Interromper à toa é pior que ficar calado. Os padrões são conservadores de propósito.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            {POLITICA_PRINCIPAL.map((c) => <CampoPolitica key={c.chave} c={c} form={form} setForm={setForm} ro={ro} />)}
          </div>
          <button type="button" className="flex items-center gap-1 text-xs text-muted-foreground" onClick={() => setAvancado(!avancado)}>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${avancado ? "rotate-180" : ""}`} /> Avançado
          </button>
          {avancado && (
            <div className="grid gap-4 sm:grid-cols-3">
              {POLITICA_AVANCADA.map((c) => <CampoPolitica key={c.chave} c={c} form={form} setForm={setForm} ro={ro} />)}
            </div>
          )}
          {!ro && cfg.data && <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, politica: cfg.data!.padroes.politica })}>Restaurar padrões</Button>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Palavras-gatilho</CardTitle>
              <CardDescription>Termos que acendem o detector sem gastar IA. Digite e Enter. Use o miolo da expressão, sem acento: "sem verba", "levar para o secretario".</CardDescription>
            </div>
            {!ro && temIa && (
              <Button variant="outline" size="sm" disabled={sugerir.isPending}
                onClick={async () => { const r = await sugerir.mutateAsync({ contexto: [form.produto, form.ticket, form.nomeEmpresa].filter(Boolean).join(", ") }); setSugestoes(r); if (!Object.values(r).some((l) => l?.length)) toast.info("A IA não encontrou termos novos além dos que já estão aqui."); }}>
                {sugerir.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-2 h-3.5 w-3.5" />} Sugerir com IA
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {sugestoes && Object.values(sugestoes).some((l) => l?.length) && (
            <div className="rounded-md border border-dashed p-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-medium">Sugestões da IA — clique para adicionar</p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={aplicarTodas}>Adicionar todas</Button>
                  <Button variant="ghost" size="sm" onClick={() => setSugestoes(null)}>Fechar</Button>
                </div>
              </div>
              <div className="space-y-1">
                {(Object.keys(sugestoes) as (keyof CopilotoGatilhos)[]).filter((k) => sugestoes[k]?.length).map((k) => (
                  <div key={k} className="flex flex-wrap items-center gap-1 text-xs">
                    <span className="mr-1 w-24 text-muted-foreground">{ROTULO_SUBTIPO[k]}</span>
                    {sugestoes[k]!.map((t) => (
                      <button key={t} type="button" className="rounded border bg-background px-2 py-0.5 hover:bg-muted" onClick={() => aplicarSugestao(k, t)}>+ {t}</button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid gap-3 lg:grid-cols-2">
            {(Object.keys(form.gatilhos) as (keyof CopilotoGatilhos)[]).map((k) => (
              <div key={k}>
                <Label className="text-xs">{ROTULO_SUBTIPO[k] ?? k} <span className="text-muted-foreground">({form.gatilhos[k].length})</span></Label>
                <Chips valor={form.gatilhos[k]} disabled={ro} placeholder="digite e Enter" onChange={(v) => setForm({ ...form, gatilhos: { ...form.gatilhos, [k]: v } })} />
              </div>
            ))}
          </div>
          {!ro && cfg.data && <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, gatilhos: cfg.data!.padroes.gatilhos })}>Restaurar padrões</Button>}
        </CardContent>
      </Card>

      {!ro && sujo && (
        <div className="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-lg border bg-background px-4 py-2 shadow-lg">
          <span className="text-sm">Alterações não salvas</span>
          <Button variant="ghost" size="sm" onClick={() => setForm(JSON.parse(salvo.current))}>Descartar</Button>
          <Button size="sm" onClick={() => salvar.mutate(form, { onSuccess: () => { salvo.current = JSON.stringify(form); } })} disabled={salvar.isPending}>Salvar</Button>
        </div>
      )}
    </div>
  );
}

// ─── Aba: Playbook ───────────────────────────────────────────────────────────

function EditorTrecho({ inicial, temIa, produtos, onFechar, onSalvar, salvando }: {
  inicial: Trecho & { id?: number }; temIa: boolean; produtos: Produto[]; onFechar: () => void; onSalvar: (t: Trecho & { id?: number }) => void; salvando: boolean;
}) {
  const [t, setT] = useState(inicial);
  const melhorar = trpc.copiloto.ia.melhorarTrecho.useMutation({
    onSuccess: (r) => { setT({ ...t, ...r }); toast.success("Trecho revisado pela IA. Confira antes de salvar."); },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onFechar()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t.id ? "Editar trecho" : "Novo trecho do playbook"}</DialogTitle>
          <DialogDescription>Escreva como você falaria. O copiloto adapta para a situação, mas só usa o que está aqui.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-[1fr_180px]">
            <div>
              <Label>Título</Label>
              <Input value={t.titulo} onChange={(e) => setT({ ...t, titulo: e.target.value })} placeholder="Ex: Inversão de orçamento pelo retorno" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={t.tipo} onValueChange={(v) => setT({ ...t, tipo: v as CopilotoChunkTipo })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{COPILOTO_CHUNK_TIPOS.map((x) => <SelectItem key={x} value={x}>{ROTULO_TIPO[x]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Vale para</Label>
            <SelectEscopo valor={t.productId} produtos={produtos} onChange={(v) => setT({ ...t, productId: v })} />
            <p className="mt-1 text-xs text-muted-foreground">Trechos da empresa entram em toda call; os de um produto só quando o vendedor escolhe esse produto ao ligar.</p>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Conteúdo</Label>
              {temIa && (
                <Button type="button" variant="ghost" size="sm" disabled={!t.conteudo || melhorar.isPending} onClick={() => melhorar.mutate({ titulo: t.titulo, conteudo: t.conteudo, tipo: t.tipo })}>
                  {melhorar.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5" />} Melhorar com IA
                </Button>
              )}
            </div>
            <Textarea rows={6} value={t.conteudo} onChange={(e) => setT({ ...t, conteudo: e.target.value })} placeholder="Cole o texto bruto e clique em Melhorar com IA, ou escreva direto." />
          </div>
          <div>
            <Label>Quando usar (uma frase)</Label>
            <Input value={t.resumoBusca} onChange={(e) => setT({ ...t, resumoBusca: e.target.value })} placeholder="Ex: quando o lead diz que não tem verba" />
          </div>
          <div>
            <Label>Responde a</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {COPILOTO_SUBTIPOS.map((x) => (
                <Button key={x} type="button" size="sm" variant={t.tags.includes(x) ? "default" : "outline"} onClick={() => setT({ ...t, tags: alternar(t.tags, x) })}>{ROTULO_SUBTIPO[x]}</Button>
              ))}
            </div>
          </div>
          <div>
            <Label>Estágios</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {COPILOTO_ESTAGIOS.map((x) => (
                <Button key={x} type="button" size="sm" variant={t.estagios.includes(x) ? "default" : "outline"} onClick={() => setT({ ...t, estagios: alternar(t.estagios, x) })}>{ROTULO_ESTAGIO[x]}</Button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={t.isActive} onCheckedChange={(v) => setT({ ...t, isActive: v })} />
            <span className="text-sm">Ativo (entra na busca)</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button onClick={() => onSalvar(t)} disabled={salvando || !t.titulo.trim() || !t.conteudo.trim()}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GerarComIa({ produtos, onFechar, onCriados }: { produtos: Produto[]; onFechar: () => void; onCriados: () => void }) {
  const [texto, setTexto] = useState("");
  const [instrucoes, setInstrucoes] = useState("");
  const [url, setUrl] = useState("");
  const [docIds, setDocIds] = useState<number[]>([]);
  const [arquivo, setArquivo] = useState<{ fileName: string; mimeType: string; contentBase64: string } | null>(null);
  const [productId, setProductId] = useState<number | null>(null);
  const [propostos, setPropostos] = useState<(Trecho & { marcado: boolean })[] | null>(null);
  const [fontesUsadas, setFontesUsadas] = useState<string[]>([]);
  const documentos = trpc.copiloto.ia.documentos.useQuery();

  const gerar = trpc.copiloto.ia.gerarPlaybook.useMutation({
    onSuccess: (r) => {
      setPropostos(r.trechos.map((t) => ({ ...t, productId, isActive: true, marcado: true })));
      setFontesUsadas(r.fontes);
      if (r.cortado) toast.info("O material era grande; a IA leu os primeiros 60 mil caracteres.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });
  const criar = trpc.copiloto.ia.criarTrechos.useMutation({
    onSuccess: (r) => { toast.success(`${r.criados} trechos adicionados ao playbook.`); onCriados(); onFechar(); },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });
  const marcados = propostos?.filter((p) => p.marcado) ?? [];
  const temFonte = texto.trim().length >= 40 || docIds.length > 0 || url.trim().length > 3 || arquivo !== null;

  const lerArquivo = (f: File | undefined) => {
    if (!f) { setArquivo(null); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("Arquivo acima de 15 MB."); return; }
    const leitor = new FileReader();
    leitor.onload = () => {
      const base64 = String(leitor.result).split(",")[1] ?? "";
      setArquivo({ fileName: f.name, mimeType: f.type || "application/octet-stream", contentBase64: base64 });
    };
    leitor.readAsDataURL(f);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onFechar()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4" /> Gerar playbook com IA</DialogTitle>
          <DialogDescription>Junte o material que você já tem — texto, documentos da Inteligência, um PDF ou um site. A IA separa em trechos; você escolhe o que entra.</DialogDescription>
        </DialogHeader>

        {!propostos && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Aplicar a</Label>
                <SelectEscopo valor={productId} produtos={produtos} onChange={setProductId} />
              </div>
              <div>
                <Label>Instruções (opcional)</Label>
                <Input value={instrucoes} onChange={(e) => setInstrucoes(e.target.value)} placeholder="ex. foque em objeções de preço; público são prefeituras" />
              </div>
            </div>

            <Tabs defaultValue="documentos">
              <TabsList>
                <TabsTrigger value="documentos">Inteligência{docIds.length ? ` (${docIds.length}/10)` : ""}</TabsTrigger>
                <TabsTrigger value="texto">Texto{texto.trim() ? " ✓" : ""}</TabsTrigger>
                <TabsTrigger value="arquivo">PDF / DOCX{arquivo ? " ✓" : ""}</TabsTrigger>
                <TabsTrigger value="site">Site{url.trim() ? " ✓" : ""}</TabsTrigger>
              </TabsList>
              <TabsContent value="documentos" className="mt-3">
                {documentos.isLoading && <p className="text-sm text-muted-foreground">Carregando documentos…</p>}
                {documentos.data?.length === 0 && (
                  <p className="text-sm text-muted-foreground">Nenhum documento pronto na Inteligência. Importe a proposta, o site ou a apresentação em <Link href="/intelligence?tab=documents" className="underline">Inteligência → Documentos</Link>, ou use as outras abas aqui.</p>
                )}
                <div className="max-h-64 space-y-1 overflow-y-auto">
                  {documentos.data?.map((d) => (
                    <label key={d.id} className="flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm hover:bg-muted/40">
                      <Checkbox
                        checked={docIds.includes(d.id)}
                        disabled={!docIds.includes(d.id) && docIds.length >= 10}
                        onCheckedChange={(v) => setDocIds(v ? [...docIds, d.id].slice(0, 10) : docIds.filter((x) => x !== d.id))}
                      />
                      <span className="min-w-0 flex-1 truncate">{d.titulo}</span>
                      <Badge variant="outline" className="text-[10px]">{d.tipo}</Badge>
                      <span className="text-xs text-muted-foreground">{Math.round(d.caracteres / 1000)} mil caracteres</span>
                    </label>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="texto" className="mt-3">
                <Textarea rows={10} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Cole aqui proposta, roteiro de vendas, lista de objeções, anotações de calls…" />
              </TabsContent>
              <TabsContent value="arquivo" className="mt-3 space-y-2">
                <Input type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => lerArquivo(e.target.files?.[0])} />
                {arquivo && <p className="text-xs text-muted-foreground">Pronto: {arquivo.fileName}</p>}
                <p className="text-xs text-muted-foreground">PDF ou Word, até 15 MB. O texto é extraído aqui e não fica salvo na Inteligência — para guardar, importe por lá.</p>
              </TabsContent>
              <TabsContent value="site" className="mt-3 space-y-2">
                <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.suaempresa.com.br/curso" />
                <p className="text-xs text-muted-foreground">A página é lida na hora (texto visível). Sites que exigem login não funcionam.</p>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={onFechar}>Cancelar</Button>
              <Button onClick={() => gerar.mutate({ texto, instrucoes, url, documentoIds: docIds, arquivo: arquivo ?? undefined, productId })} disabled={!temFonte || gerar.isPending}>
                {gerar.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} {gerar.isPending ? "Lendo o material…" : "Gerar trechos"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {propostos && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {propostos.length} trechos propostos a partir de {fontesUsadas.join(", ")}. Vão para <strong>{productId === null ? "Empresa" : produtos.find((p) => p.id === productId)?.nome ?? "produto"}</strong>. Desmarque o que não serve; depois dá para editar cada um.
            </p>
            <div className="space-y-2">
              {propostos.map((p, i) => (
                <label key={i} className={`flex cursor-pointer gap-3 rounded-md border p-3 ${p.marcado ? "" : "opacity-50"}`}>
                  <Checkbox checked={p.marcado} onCheckedChange={(v) => setPropostos(propostos.map((x, j) => (j === i ? { ...x, marcado: Boolean(v) } : x)))} className="mt-1" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{p.titulo}</span>
                      <Badge variant="secondary">{ROTULO_TIPO[p.tipo]}</Badge>
                      {p.tags.map((t) => <Badge key={t} variant="outline">{ROTULO_SUBTIPO[t]}</Badge>)}
                    </div>
                    {p.resumoBusca && <p className="mt-0.5 text-xs text-muted-foreground">Quando usar: {p.resumoBusca}</p>}
                    <p className="mt-1 text-sm">{p.conteudo}</p>
                  </div>
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPropostos(null)}>Voltar</Button>
              <Button onClick={() => criar.mutate({ trechos: marcados.map(({ marcado: _m, ...t }) => t) })} disabled={marcados.length === 0 || criar.isPending}>
                Adicionar {marcados.length} {marcados.length === 1 ? "trecho" : "trechos"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AbaPlaybook({ podeGerenciar, temIa }: { podeGerenciar: boolean; temIa: boolean }) {
  const utils = trpc.useUtils();
  const lista = trpc.copiloto.playbook.list.useQuery();
  const invalidar = () => { void utils.copiloto.playbook.list.invalidate(); void utils.copiloto.status.invalidate(); };
  const erro = (err: unknown) => toast.error(toUserFriendlyError(err));

  // O playbook que está na tela — é o que o Assistente consulta antes de
  // cadastrar um trecho novo, para não duplicar o que já existe.
  useContextoDeLista({
    titulo: "Playbook do copiloto de call",
    itens: (lista.data ?? []).map((c: any) => ({
      id: c.id, titulo: c.titulo, tipo: c.tipo, ativo: c.isActive, etapas: c.estagios, tags: c.tags,
    })),
    amostra: 25,
  });


  const criar = trpc.copiloto.playbook.create.useMutation({ onSuccess: () => { invalidar(); setEditando(null); toast.success("Trecho criado."); }, onError: erro });
  const atualizar = trpc.copiloto.playbook.update.useMutation({ onSuccess: () => { invalidar(); setEditando(null); }, onError: erro });
  const excluir = trpc.copiloto.playbook.delete.useMutation({ onSuccess: () => { invalidar(); setExcluindo(null); toast.success("Trecho excluído."); }, onError: erro });
  const exemplo = trpc.copiloto.playbook.carregarExemplo.useMutation({
    onSuccess: (r) => { invalidar(); toast.success(r.criados ? `${r.criados} trechos de exemplo carregados.` : "O exemplo já estava carregado."); },
    onError: erro,
  });

  const produtos = trpc.copiloto.playbook.produtos.useQuery(undefined, { staleTime: 60_000 });
  const listaProdutos: Produto[] = produtos.data ?? [];
  const nomeProduto = (id: number | null) => (id === null ? "Empresa" : listaProdutos.find((p) => p.id === id)?.nome ?? `Produto #${id}`);
  const [busca, setBusca] = useState("");
  const [tipo, setTipo] = useState("todos");
  const [escopo, setEscopo] = useState("todos");
  const [editando, setEditando] = useState<(Trecho & { id?: number }) | null>(null);
  const [excluindo, setExcluindo] = useState<{ id: number; titulo: string } | null>(null);
  const [gerando, setGerando] = useState(false);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return (lista.data ?? []).filter((c) =>
      (tipo === "todos" || c.tipo === tipo) &&
      (escopo === "todos" || (escopo === ESCOPO_EMPRESA ? c.productId === null : c.productId === Number(escopo))) &&
      (!q || `${c.titulo} ${c.resumoBusca} ${c.conteudo} ${c.tags.join(" ")}`.toLowerCase().includes(q)));
  }, [lista.data, busca, tipo, escopo]);

  const salvarTrecho = (t: Trecho & { id?: number }) => {
    const { id, ...dados } = t;
    if (id) atualizar.mutate({ id, ...dados });
    else criar.mutate(dados);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Buscar no playbook…" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <Select value={tipo} onValueChange={setTipo}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os tipos</SelectItem>
            {COPILOTO_CHUNK_TIPOS.map((x) => <SelectItem key={x} value={x}>{ROTULO_TIPO[x]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={escopo} onValueChange={setEscopo}>
          <SelectTrigger className="w-[190px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Empresa e produtos</SelectItem>
            <SelectItem value={ESCOPO_EMPRESA}>Só empresa</SelectItem>
            {listaProdutos.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        {podeGerenciar && (
          <>
            {temIa && <Button variant="outline" onClick={() => setGerando(true)}><Sparkles className="mr-2 h-4 w-4" /> Gerar com IA</Button>}
            {lista.data?.length === 0 && <Button variant="outline" onClick={() => exemplo.mutate()} disabled={exemplo.isPending}>Carregar exemplo</Button>}
            <Button onClick={() => setEditando({ ...TRECHO_VAZIO })}><Plus className="mr-2 h-4 w-4" /> Novo trecho</Button>
          </>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        <BookOpen className="mr-1 inline h-3.5 w-3.5" /> O único material que o copiloto usa. Trechos da empresa valem em toda call; os de um produto entram quando o vendedor escolhe o produto ao ligar. Sem cobertura, ele responde "sem cobertura" em vez de inventar.
        {lista.data && <> · {lista.data.filter((c) => c.isActive).length} ativos de {lista.data.length}</>}
      </p>

      {lista.isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {lista.data?.length === 0 && (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">
          Playbook vazio. {temIa ? "Cole a sua proposta ou roteiro em “Gerar com IA”, ou carregue o exemplo para ver o copiloto funcionando." : "Carregue o exemplo para ver o copiloto funcionando e depois troque pelo seu material."}
        </CardContent></Card>
      )}
      {lista.data && lista.data.length > 0 && filtrados.length === 0 && <p className="text-sm text-muted-foreground">Nada encontrado com esse filtro.</p>}

      <div className="divide-y rounded-md border bg-card">
        {filtrados.map((c) => (
          <div key={c.id} className={`group flex items-start gap-3 px-3 py-2.5 ${c.isActive ? "" : "opacity-60"}`}>
            <button type="button" className="min-w-0 flex-1 text-left" onClick={() => podeGerenciar && setEditando({ id: c.id, productId: c.productId, titulo: c.titulo, tipo: c.tipo as CopilotoChunkTipo, estagios: c.estagios as CopilotoEstagio[], tags: c.tags as Trecho["tags"], resumoBusca: c.resumoBusca, conteudo: c.conteudo, isActive: c.isActive })}>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-medium">{c.titulo}</span>
                <Badge variant={c.productId === null ? "outline" : "default"} className="text-[10px]">{nomeProduto(c.productId)}</Badge>
                <Badge variant="secondary" className="text-[10px]">{ROTULO_TIPO[c.tipo] ?? c.tipo}</Badge>
                {c.tags.map((t) => <Badge key={t} variant="outline" className="text-[10px]">{ROTULO_SUBTIPO[t] ?? t}</Badge>)}
                {c.estagios.length > 0 && <span className="text-[10px] text-muted-foreground">{c.estagios.map((e) => ROTULO_ESTAGIO[e] ?? e).join(" · ")}</span>}
              </div>
              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{c.resumoBusca ? `${c.resumoBusca} — ` : ""}{c.conteudo}</p>
            </button>
            {podeGerenciar && (
              <div className="flex shrink-0 items-center gap-1">
                <Switch checked={c.isActive} onCheckedChange={(v) => atualizar.mutate({ id: c.id, isActive: v })} aria-label="ativo" />
                <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100" onClick={() => setEditando({ id: c.id, productId: c.productId, titulo: c.titulo, tipo: c.tipo as CopilotoChunkTipo, estagios: c.estagios as CopilotoEstagio[], tags: c.tags as Trecho["tags"], resumoBusca: c.resumoBusca, conteudo: c.conteudo, isActive: c.isActive })}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100" onClick={() => setExcluindo({ id: c.id, titulo: c.titulo })}><Trash2 className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {editando && <EditorTrecho inicial={editando} temIa={temIa} produtos={listaProdutos} onFechar={() => setEditando(null)} onSalvar={salvarTrecho} salvando={criar.isPending || atualizar.isPending} />}
      {gerando && <GerarComIa produtos={listaProdutos} onFechar={() => setGerando(false)} onCriados={invalidar} />}

      <AlertDialog open={!!excluindo} onOpenChange={(o) => !o && setExcluindo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir "{excluindo?.titulo}"?</AlertDialogTitle>
            <AlertDialogDescription>As sugestões antigas continuam nos insights, mas o copiloto para de usar este trecho. Para só pausar, desligue a chave.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => excluindo && excluir.mutate({ id: excluindo.id })}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Pagina ──────────────────────────────────────────────────────────────────

function CopilotoContent() {
  const status = trpc.copiloto.status.useQuery(undefined, { staleTime: 30_000 });
  const s = status.data;
  const search = useSearch();
  const [, navigate] = useLocation();
  const aba = new URLSearchParams(search).get("tab") ?? "playbook";
  const temIa = s?.llm !== "nenhum";

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Copiloto de call</h1>
          <p className="mt-1 text-muted-foreground">Transcreve a reunião ao vivo e sugere o script do playbook no momento certo.</p>
        </div>
        <Button asChild variant="outline"><Link href="/copiloto/insights"><BarChart3 className="mr-2 h-4 w-4" /> Insights</Link></Button>
      </div>

      {s && (
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant={s.habilitadoNoPlano ? "secondary" : "destructive"}>{s.habilitadoNoPlano ? "Habilitado no plano" : "Não habilitado no plano"}</Badge>
          <Badge variant={s.ligado ? "secondary" : "outline"}>{s.ligado ? "Ligado" : "Desligado"}</Badge>
          <Badge variant={s.transcricaoConfigurada ? "secondary" : "destructive"}>{s.transcricaoConfigurada ? "Transcrição ok" : "Transcrição não configurada"}</Badge>
          <Badge variant={s.llm === "nenhum" ? "destructive" : "secondary"}>{s.llm === "workspace" ? "IA: chave do workspace" : s.llm === "global" ? "IA: chave do Lead Conversa" : "IA não configurada"}</Badge>
        </div>
      )}

      {s && !s.habilitadoNoPlano && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900">O copiloto de call não está habilitado no plano deste workspace. Um administrador do Lead Conversa libera a feature "Copiloto de call ao vivo" no plano.</CardContent>
        </Card>
      )}

      {s && (
        <Tabs value={aba} onValueChange={(v) => navigate(`/copiloto?tab=${v}`, { replace: true })}>
          <TabsList>
            <TabsTrigger value="playbook">Playbook{s.totalChunks ? ` (${s.chunksAtivos})` : ""}</TabsTrigger>
            <TabsTrigger value="regras">Regras</TabsTrigger>
            <TabsTrigger value="extensao">Extensão</TabsTrigger>
          </TabsList>
          <TabsContent value="playbook" className="mt-4"><AbaPlaybook podeGerenciar={s.podeGerenciar} temIa={temIa} /></TabsContent>
          <TabsContent value="regras" className="mt-4"><AbaRegras podeGerenciar={s.podeGerenciar} temIa={temIa} /></TabsContent>
          <TabsContent value="extensao" className="mt-4"><AbaExtensao wsUrl={s.wsUrl} usuario={s.usuario} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

export default function Copiloto() {
  return (
    <PermissionGuard permission="copilot:use">
      <DashboardLayout>
        <CopilotoContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
